import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchUserLocation } from "@/lib/geo-location/locationFetcher";
import type { UserLocation } from "@/lib/geo-location/types";

type CachedUserLocation = {
  location: UserLocation;
  updatedAt: number;
};

type UseUserLocationOptions = {
  enabled?: boolean;
};

const USER_LOCATION_STORAGE_KEY = "tovtrip_user_location";
const USER_LOCATION_TTL_MS = 15 * 60 * 1000;

/**
 * Read a valid cached location from the current browser tab.
 * @returns
 */
const readCachedUserLocation = (): CachedUserLocation | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = sessionStorage.getItem(USER_LOCATION_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const cachedValue = JSON.parse(rawValue) as Partial<CachedUserLocation>;
    const isValid = Boolean(
      cachedValue.location &&
      typeof cachedValue.location.city === "string" &&
      typeof cachedValue.location.country === "string" &&
      typeof cachedValue.updatedAt === "number" &&
      Date.now() - cachedValue.updatedAt < USER_LOCATION_TTL_MS,
    );

    if (!isValid) {
      sessionStorage.removeItem(USER_LOCATION_STORAGE_KEY);
      return null;
    }

    return cachedValue as CachedUserLocation;
  } catch {
    sessionStorage.removeItem(USER_LOCATION_STORAGE_KEY);
    return null;
  }
};

/**
 * Resolve and cache the user's current location.
 * @returns
 */
const fetchAndCacheUserLocation = async (): Promise<UserLocation> => {
  // Request a fresh browser or IP-based location.
  const location = await fetchUserLocation();

  // Cache the resolved location for refresh-safe reuse in this tab.
  if (typeof window !== "undefined") {
    sessionStorage.setItem(
      USER_LOCATION_STORAGE_KEY,
      JSON.stringify({ location, updatedAt: Date.now() }),
    );
  }

  return location;
};

/**
 * Get the user's location with a short-lived tab-scoped cache.
 * @param options
 * @returns
 */
export const useUserLocation = ({
  enabled = false,
}: UseUserLocationOptions = {}) => {
  const queryClient = useQueryClient();
  const [isHydrated, setIsHydrated] = useState(false);

  // Hydrate cached browser state only after the initial client render.
  useEffect(() => {
    const cachedLocation = readCachedUserLocation();
    if (cachedLocation) {
      queryClient.setQueryData(["user-location"], cachedLocation.location, {
        updatedAt: cachedLocation.updatedAt,
      });
    }
    setIsHydrated(true);
  }, [queryClient]);

  return useQuery<UserLocation>({
    queryKey: ["user-location"],
    queryFn: fetchAndCacheUserLocation,
    enabled: enabled && isHydrated,
    retry: false,
    staleTime: USER_LOCATION_TTL_MS,
    gcTime: USER_LOCATION_TTL_MS,
  });
};
